import { useEffect, useLayoutEffect, useRef } from 'react';
import {
  useInfiniteHits,
  UseInfiniteHitsProps,
  useInstantSearch,
} from 'react-instantsearch-hooks-web';
import ProductGridItem from '@/components/product/GridItem';
import type { Hit } from 'instantsearch.js';
import { createInfiniteHitsSessionStorageCache } from 'react-instantsearch-dom';
import {
  MaterializedContentBlock,
  SimpleMaterializedArticlePage,
} from '@fagmobler/sanity';
import { ContentBlocksRenderer } from '@/components/contentBlocks/ContentBlocksRenderer';
import clsx from 'clsx';
import { CampaignCard } from '@fagmobler/ui';
import Image from 'next/image';
import Link from 'next/link';
import { pl } from 'date-fns/locale';
const cache = createInfiniteHitsSessionStorageCache();

type ProductListInstantSearchProps = UseInfiniteHitsProps & {
  infiniteScroll?: boolean;
  contentBlocks?: MaterializedContentBlock[] | null;
  relatedArticles?: SimpleMaterializedArticlePage[] | null;
};

const CONTENTBLOCKS_DENSITY = 16;
const CONTENTBLOCKS_OFFSET = 4;

export const ProductListInstantSearch = (
  props: ProductListInstantSearchProps
) => {
  const { infiniteScroll, contentBlocks, relatedArticles, ...infiniteProps } =
    props;
  const { hits, isLastPage, showMore, sendEvent } = useInfiniteHits({
    ...infiniteProps,
    cache,
  });

  const sentinelRef = useRef(null);
  const productWrapperRef = useRef<HTMLUListElement | null>(null);

  // We have manually do the scroll restoration in a layout effect after algolia has rendered the results on the page
  // Maybe in the future we use nextjs router events to do this
  const scrollKey = useRef(null);

  useEffect(() => {
    scrollKey.current = window.history.state.key;
  }, []);

  useLayoutEffect(() => {
    if (hits.length === 0 || !scrollKey.current) return;

    const item = `__next_scroll_${scrollKey.current}`;
    const scrollHistoryItem = sessionStorage.getItem(item);
    if (scrollHistoryItem && scrollKey.current) {
      const y = JSON.parse(scrollHistoryItem)?.y;
      window.scrollTo(0, y);
      scrollKey.current = null;
      sessionStorage.removeItem(scrollHistoryItem);
    }
  }, [hits]);

  useEffect(() => {
    if (sentinelRef.current !== null) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const renderedProductCards = productWrapperRef.current?.childNodes;

          if (
            entry.isIntersecting &&
            !isLastPage &&
            showMore &&
            renderedProductCards &&
            renderedProductCards.length > 5
          ) {
            showMore();
          }
        });
      });

      observer.observe(sentinelRef.current);

      return () => {
        observer.disconnect();
      };
    }
  }, [isLastPage, showMore]);

  const handleClick = (hit: Hit) => {
    sendEvent('click', hit, 'clickedObjectIDsAfterSearch');
  };

  const pluginItems = [...(contentBlocks || []), ...(relatedArticles || [])];

  return (
    <ul
      className={clsx(
        'mt-5',
        'grid gap-5',
        'grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4',
        'sm:grid-flow-row-dense'
      )}
      ref={productWrapperRef}
    >
      {hits.map((hit, i) => {
        const adjustedIndex = i - CONTENTBLOCKS_OFFSET;
        const pluginIndex = Math.floor(adjustedIndex / CONTENTBLOCKS_DENSITY);
        const pluginItem =
          adjustedIndex >= 0 &&
          adjustedIndex % CONTENTBLOCKS_DENSITY === 0 &&
          pluginItems &&
          pluginItems.length > pluginIndex &&
          pluginItems[pluginIndex];

        let colSpan = 2;

        if (
          pluginItem &&
          'imageWithPoints' in pluginItem &&
          pluginItem.imageWithPoints?.contentBlockSettings?.colSpan
        ) {
          colSpan =
            pluginItem.imageWithPoints.contentBlockSettings.colSpan ?? 2;
        }
        if (
          pluginItem &&
          'contentBlockSettings' in pluginItem &&
          pluginItem.contentBlockSettings?.colSpan
        ) {
          colSpan = pluginItem.contentBlockSettings.colSpan ?? 2;
        }

        return (
          <>
            {pluginItem && pluginItem._type !== 'page' && (
              <div
                key={pluginItem._key}
                className={clsx(
                  'relative',
                  'overflow-hidden',
                  'place-self-stretch',
                  'rounded',
                  'flex',
                  'col-span-2',
                  {
                    'md:col-span-2': colSpan == 2,
                    'md:cols-span-3': colSpan == 3,
                    // Large span 2 even
                    'lg:col-start-1 lg:col-end-3':
                      colSpan == 2 && pluginIndex % 2 == 0,
                    // large span 2 odd
                    'lg:col-start-3 lg:col-end-5':
                      colSpan == 2 && pluginIndex % 2 != 0,
                    // span 3 large even
                    'lg:col-start-1 lg:col-end-4':
                      colSpan == 3 && pluginIndex % 2 == 0,
                    // span 3 large odd
                    'lg:col-start-2 lg:col-end-5':
                      colSpan == 3 && pluginIndex % 2 != 0,
                    'col-span-full': colSpan == -1, // full width
                  }
                )}
              >
                <ContentBlocksRenderer
                  blocks={[pluginItem]}
                ></ContentBlocksRenderer>
              </div>
            )}
            {pluginItem &&
              pluginItem._type === 'page' &&
              pluginItem.title &&
              pluginItem.mainImage &&
              pluginItem.mainImage.width &&
              pluginItem.mainImage.height &&
              pluginItem.slug?.current && (
                <Link
                  href={`/inspirasjon/${pluginItem.slug.current}`}
                  key={pluginItem._id}
                  className={clsx(
                    'block w-full h-full relative rounded overflow-hidden',
                    'col-span-2 md:col-span-3',
                    {
                      // Large span 2 even
                      'lg:col-start-1 lg:col-end-3':
                        colSpan == 2 && pluginIndex % 2 == 0,
                      // large span 2 odd
                      'lg:col-start-3 lg:col-end-5':
                        colSpan == 2 && pluginIndex % 2 != 0,
                      // span 3 large even
                    }
                  )}
                >
                  <CampaignCard
                    title={pluginItem.title}
                    image={
                      pluginItem.mainImage.croppedImageUrl && (
                        <Image
                          src={pluginItem.mainImage.croppedImageUrl}
                          alt={pluginItem.mainImage.alt || pluginItem.title}
                          width={pluginItem.mainImage.width}
                          height={pluginItem.mainImage.height}
                          placeholder={
                            pluginItem.mainImage.lqip ? 'blur' : undefined
                          }
                          blurDataURL={pluginItem.mainImage.lqip || undefined}
                        />
                      )
                    }
                    fill
                    variant="overlay"
                  />
                </Link>
              )}

            <li key={hit.objectID}>
              <ProductGridItem
                product={hit}
                size="lg"
                priority={i < 8}
                imageSizes="(max-width: 640px) 50vw, (max-width: 768px) 33vw, (max-width: 1024px) 25vw, 384px"
                onClick={() => handleClick(hit)}
              />
            </li>
          </>
        );
      })}
      <li ref={sentinelRef} aria-hidden="true" />
    </ul>
  );
};

export default ProductListInstantSearch;
